import React from "react";
import ServicePageLayout, { H2, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Business WiFi Lethbridge | Commercial WiFi Installation Alberta | Epicom Systems",
  metaDescription:
    "Commercial Wi-Fi design and installation across Southern Alberta. Engineered coverage for offices, retail, industrial spaces and multi-building sites — built on Ubiquiti UniFi.",
  canonicalPath: "/business-wifi",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/business-wifi#service",
    "name": "Business Wi-Fi Installation",
    "serviceType": "Commercial Wi-Fi design and installation",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Commercial Wi-Fi design and installation across Southern Alberta — engineered coverage for offices, retail, industrial and multi-building sites, built around Ubiquiti UniFi.",
    "url": "https://epicom.ca/business-wifi",
  },
};

export default function BusinessWiFi() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Business Wi-Fi"
      h1="Commercial Wi-Fi Built to Actually Work"
      intro="Business Wi-Fi is where a lot of network jobs succeed or fail. Consumer mesh gear plunked in the middle of an office building will always leave a dead corner, drop the guest network at random, and make somebody’s life miserable. Epicom designs and installs commercial Wi-Fi engineered for the actual space — coverage, capacity and management, done properly."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/unifi-installation", label: "UniFi Installation" },
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
        { to: "/cellular-das", label: "Cellular DAS" },
      ]}
    >
      <H2>What sets a real commercial install apart</H2>
      <UL>
        <><strong className="text-white/90">Placement, not guesswork</strong> — access points go where the floor plan and the RF say they should, not next to the nearest outlet.</>
        <><strong className="text-white/90">Ethernet backhaul + PoE</strong> — every AP is properly wired and powered off a switch, not daisy-chained through a wireless repeater.</>
        <><strong className="text-white/90">Capacity, not just coverage</strong> — an AP might reach the whole floor and still fall over at 30 simultaneous users. We plan for how many devices actually need to work, not just where the signal appears.</>
        <><strong className="text-white/90">Segmentation done right</strong> — separate SSIDs and VLANs for staff, guests, IoT devices and point-of-sale so they’re not fighting each other.</>
        <><strong className="text-white/90">Roaming that works</strong> — fast handoff between APs so phones, tablets and Wi-Fi calling actually survive walking across the building.</>
        <><strong className="text-white/90">One management pane</strong> — the whole environment is visible in a single UniFi controller instead of five separate portals.</>
      </UL>

      <H2>Common project types</H2>
      <UL>
        <>Offices and professional practices where staff Wi-Fi and guest Wi-Fi both need to be reliable</>
        <>Retail spaces with point-of-sale, staff devices and customer Wi-Fi</>
        <>Industrial and manufacturing facilities where coverage has to punch through shop bays and yards</>
        <>Farms and acreages that want real coverage across the house, shop and outbuildings</>
        <>Multi-building sites where <a href="/point-to-point-wireless" className="text-signal hover:underline">wireless bridges</a> tie separate buildings into one network</>
        <>Home Wi-Fi overhauls where a commercial-grade install just makes more sense than another consumer mesh</>
      </UL>

      <H2>Built on UniFi</H2>
      <P>
        Most of our Wi-Fi installs are built on Ubiquiti UniFi access points because they
        hit the right combination of performance, capability and ongoing cost for the kind
        of work we do. That means the Wi-Fi ties directly into the rest of the UniFi
        network — switching, routing, cameras and access — as one integrated environment
        rather than a collection of parts. See our <a href="/unifi-installation" className="text-signal hover:underline">UniFi Installation</a> page
        for the full picture.
      </P>

      <H2>What to expect on a Wi-Fi quote</H2>
      <P>
        We start with a site walk or floor plan review, understand how the space is used
        (staff, guests, devices, applications, hours), and then design the coverage. Quotes
        include the access points, the switch and cabling needed to feed them, and any
        gateway or controller changes required to run it all cleanly. No surprises, and
        no “oh yeah, you also need this” calls after the install.
      </P>
    </ServicePageLayout>
  );
}
